import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../../lib/auth'
import { useCollection, useDocument } from '../../lib/firestore'
import { useTourAccess } from '../../lib/tours'
import { countdown, formatDay, formatDayLong } from '../../lib/format'
import type { Flight, Show, Tour, TourCrew, WithId } from '../../types/models'
import Icon from '../../components/Icon'
import type { IconName } from '../../components/Icon'
import { CircleButton } from '../../components/mobile/MobileHeader'
import { TAB_BAR_INSET } from '../../components/mobile/TabBar'
import Empty from '../../components/Empty'
import TourDialog from '../../dialogs/TourDialog'

/**
 * The tour hub.
 *
 * A full-bleed poster that dissolves into the page, then the numbers that
 * matter and a route into every section. The poster is the tour's identity —
 * a plain list threw that away.
 */
export default function MobileTourHub() {
  const { tourId } = useParams<{ tourId: string }>()
  const navigate = useNavigate()
  const { uid } = useAuth()

  const { data: tour, loading } = useDocument<Tour>('tours', tourId)
  const access = useTourAccess(tour, uid)
  const canEdit = access?.canEdit ?? false

  const shows = useCollection<Show>('shows', tourId ? [where('tourId', '==', tourId)] : null)
  const crew = useCollection<TourCrew>('tourCrew', tourId ? [where('tourId', '==', tourId)] : null)
  const flights = useCollection<Flight>('flights', tourId ? [where('tourId', '==', tourId)] : null)

  const [editing, setEditing] = useState(false)

  if (loading) return <div style={{ padding: 30 }} />
  if (!tour) {
    return (
      <div style={{ padding: '30px var(--gutter)' }}>
        <Empty title="Tour not found" message="It may have been deleted." />
      </div>
    )
  }

  if (access && access.state.kind !== 'open') {
    const gate = access.state
    return (
      <div style={{ padding: '30px var(--gutter)' }}>
        <div className="panel panel-pad stack" style={{ alignItems: 'center', padding: 40 }}>
          <span className="t-eyebrow">Access</span>
          <h2 className="t-display-s">
            {gate.kind === 'pending' ? 'Not open yet' : 'Access ended'}
          </h2>
          <p className="t-caption" style={{ textAlign: 'center' }}>
            {gate.kind === 'pending'
              ? `Your access opens ${formatDayLong(gate.startsOn)}.`
              : `Your access ended ${formatDayLong(gate.endedOn)}.`}
          </p>
        </div>
      </div>
    )
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const nextShow = [...shows.data]
    .sort((a, b) => a.date.toMillis() - b.date.toMillis())
    .find((show) => show.date.toDate() >= today)

  const daysOut = daysUntil(tour)
  const started = tour.startDate.toDate() <= new Date()

  return (
    <div style={{ paddingBottom: TAB_BAR_INSET }}>
      {/* ----------------------------------------------------------- hero */}
      <div style={{ position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: 'max(10px, env(safe-area-inset-top))',
            left: 'var(--gutter)',
            right: 'var(--gutter)',
            zIndex: 10,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <CircleButton icon="chevronLeft" label="Back" onClick={() => navigate('/tours')} />
          {canEdit && (
            <CircleButton icon="edit" label="Edit tour" onClick={() => setEditing(true)} />
          )}
        </div>

        <TourPoster url={tour.posterURL} />
      </div>

      <div
        className="stack"
        style={{ padding: '0 var(--gutter)', gap: 22, marginTop: -34, position: 'relative' }}
      >
        <div className="stack" style={{ gap: 4 }}>
          <span className="t-eyebrow">{tour.artist}</span>
          <h1 className="t-display-l" style={{ lineHeight: 1.02 }}>
            {tour.tourName}
          </h1>
          <span className="t-spec-caption dim">
            {formatDay(tour.startDate)} – {formatDay(tour.endDate)} · {shows.data.length} show
            {shows.data.length === 1 ? '' : 's'}
          </span>
        </div>

        {nextShow && (
          <Link
            to={`/tours/${tour.id}/shows/${nextShow.id}`}
            className="panel panel-signalled"
            style={{ display: 'block', padding: 16 }}
          >
            <span className="row" style={{ gap: 7, marginBottom: 8 }}>
              <span className="dot dot-accent" />
              <span className="t-eyebrow accent">Next show</span>
            </span>
            <div className="spread" style={{ gap: 12, alignItems: 'flex-start' }}>
              <div className="stack" style={{ gap: 2, minWidth: 0 }}>
                <span className="t-display-s">{nextShow.city}</span>
                <span className="t-caption truncate">{nextShow.venueName}</span>
                <span className="t-spec-caption faint">
                  {formatDayLong(nextShow.date, nextShow.timezone)}
                </span>
              </div>
              <span className="chip" style={{ flex: 'none' }}>
                {countdown(nextShow.date)}
              </span>
            </div>
          </Link>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <StatTile icon="mic" value={shows.data.length} label="Shows" />
          <StatTile icon="plane" value={flights.data.length} label="Flights" />
          <StatTile icon="people" value={crew.data.length} label="Crew" />
          <StatTile
            icon="calendar"
            value={daysOut}
            label={started ? 'Days left' : 'Days out'}
            emphasised
          />
        </div>

        <NavGroup
          label="Schedule"
          entries={[
            { icon: 'mic', label: 'Shows', to: `/tours/${tour.id}/shows` },
            { icon: 'calendar', label: 'Itinerary', to: `/tours/${tour.id}/schedule` },
          ]}
        />
        <NavGroup
          label="Travel"
          entries={[
            { icon: 'plane', label: 'Flights', to: `/tours/${tour.id}/travel` },
            { icon: 'bed', label: 'Hotels', to: `/tours/${tour.id}/travel` },
          ]}
        />
        <NavGroup
          label="People"
          entries={[{ icon: 'people', label: 'Crew', to: `/tours/${tour.id}/crew` }]}
        />
      </div>

      {editing && uid && (
        <TourDialog
          open
          tour={tour}
          ownerId={uid}
          onClose={() => setEditing(false)}
          onDeleted={() => navigate('/tours', { replace: true })}
        />
      )}
    </div>
  )
}

/**
 * The artwork, dissolving into the page.
 *
 * The wash ends on the page colour exactly, so the hero has no bottom edge —
 * it simply becomes the page, and the title below sits on solid background
 * rather than on the image. That's why the title follows the skin instead of
 * staying white.
 */
function TourPoster({ url }: { url?: string | null }) {
  return (
    <div
      style={{
        position: 'relative',
        height: 'min(62vh, 430px)',
        background: url
          ? 'var(--panel-raised)'
          : 'linear-gradient(150deg, var(--accent-wash), var(--panel-raised))',
        overflow: 'hidden',
      }}
    >
      {url && (
        <img
          src={url}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to top, var(--bg) 0%, var(--bg) 12%, transparent 62%)',
        }}
      />
    </div>
  )
}

function StatTile({
  icon,
  value,
  label,
  emphasised,
}: {
  icon: IconName
  value: number
  label: string
  emphasised?: boolean
}) {
  return (
    <div
      className="panel stack"
      style={{ alignItems: 'center', gap: 3, padding: '12px 4px' }}
    >
      <Icon name={icon} size={16} style={{ color: 'var(--accent-text)' }} />
      <span
        style={{
          fontFamily: 'var(--font-spec)',
          fontWeight: 600,
          fontSize: '1.15rem',
          color: emphasised ? 'var(--accent-text)' : undefined,
        }}
      >
        {value}
      </span>
      <span className="t-eyebrow" style={{ fontSize: '0.55rem' }}>
        {label}
      </span>
    </div>
  )
}

function NavGroup({
  label,
  entries,
}: {
  label: string
  entries: Array<{ icon: IconName; label: string; to: string }>
}) {
  return (
    <section className="stack" style={{ gap: 8 }}>
      <span className="t-eyebrow" style={{ paddingInline: 2 }}>
        {label}
      </span>
      <div className="panel rows">
        {entries.map((entry) => (
          <Link
            key={entry.label}
            to={entry.to}
            className="row"
            style={{ padding: '14px 16px', gap: 13 }}
          >
            <Icon name={entry.icon} size={19} style={{ color: 'var(--accent-text)' }} />
            <span className="grow">{entry.label}</span>
            <Icon
              name="chevronRight"
              size={15}
              style={{ color: 'var(--text-tertiary)' }}
            />
          </Link>
        ))}
      </div>
    </section>
  )
}

function daysUntil(tour: WithId<Tour>): number {
  const day = 86_400_000
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = tour.startDate.toDate()
  start.setHours(0, 0, 0, 0)
  const end = tour.endDate.toDate()
  end.setHours(0, 0, 0, 0)
  if (today < start) return Math.round((start.getTime() - today.getTime()) / day)
  return Math.max(0, Math.round((end.getTime() - today.getTime()) / day))
}
